import { useMemo, useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import apiClient from "../../api/apiClient";
import { ShowData } from "./Show";
import { ExpenseData } from "./Expense";
import { ColDef } from "@ag-grid-community/core";
import { shortDateString } from "../../utils/dateFormatters";
import { AgentViewDataRow, AgentViewDataRowAdapter } from "./AgentViewDataRow";
import { formatPercentage } from "../../utils/numberFormatters";

const AgentViewAnalytics = () => {
  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const [rowData, setRowData] = useState<AgentViewDataRow[]>();

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      aggFunc: "count",
      minWidth: 200,
      suppressAutoSize: false,
      filter: true,
      resizable: true,
      enableRowGroup: true,
    };
  }, []);

  const [columnDefs, _] = useState<ColDef[]>([
    {
      field: "performanceDate",
      headerName: "Date",
      cellRenderer: "agGroupCellRenderer",
      sort: "desc",
      minWidth: 150,
      type: "dateString",
      valueFormatter: (p: any) => {
        return shortDateString(p.value);
      },
    },
    { field: "venue", headerName: "Venue" },
    { field: "city", headerName: "City", minWidth: 125 },
    { field: "state", headerName: "State", minWidth: 125 },

    {
      field: "guarantee",
      aggFunc: "sum",
      headerName: "Guarantee",
      valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
      minWidth: 150,
      filter: false,
    },
    { field: "dealType", headerName: "Deal Type", minWidth: 150 },
    {
      field: "profitSharePercent",
      headerName: "Rev Split",
      aggFunc: "avg",
      minWidth: 100,
      valueFormatter: (p: any) =>
        p.value ? formatPercentage(p.value) : "N/A",
      filter: false,
    },
    {
      field: "productionBuyouts",
      headerName: "Production Buyouts",
      aggFunc: "sum",
      valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
      filter: false,
    },
    {
      field: "walkoutPotential",
      aggFunc: "sum",
      headerName: "Walkout Potential",
      valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
      minWidth: 175,
      filter: false,
    },
    {
        field: "grossTicketRevenuePotential",
        headerName: "Gross BO",
        aggFunc: "sum",
        minWidth: 130,
        valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
        filter: false,
      },
    {
        field: "ticketRevenuePotential",
        headerName: "Net BO",
        aggFunc: "sum",
        minWidth: 130,
        valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
        filter: false,
      },
    {
      field: "sellableCap",
      aggFunc: "sum",
      headerName: "Sellable Cap",
      valueFormatter: (p: any) => Math.floor(p.value).toLocaleString(),
      minWidth: 150,
      filter: false,
    },
    {
        field: "ticketFeeTotalPotential",
        headerName: "BO Fees",
        aggFunc: "sum",
        minWidth: 130,
        valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
        filter: false,
      },
      {
        field: "supportBudget",
        headerName: "Support Budget",
        aggFunc: "sum",
        minWidth: 130,
        valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
        filter: false,
      },
  
    { field: "appearingWith", headerName: "Appearing With" },
    {
        field: "hospitalityBudget",
        headerName: "Hospitality Budget",
        aggFunc: "sum",
        minWidth: 130,
        valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
        filter: false,
      },
      {
      field: "expensesBudget",
      headerName: "Expenses (Forecast)",
      aggFunc: "sum",
      minWidth: 200,
      valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
      filter: false,
    },
    {
      field: "showScore",
      headerName: "Score",
      aggFunc: "avg",
      minWidth: 100,
      valueFormatter: (p: any) =>
        p.value ? formatPercentage(p.value) : "N/A",
      filter: false,
    },
  ]);

  const getData = async () => {
    try {
      const shows: ShowData[] = await apiClient
        .get("/show", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        })
        .then((resp) => resp.data);
      const expenses: ExpenseData[] = await apiClient
        .get("/expense", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        })
        .then((resp) => resp.data);

      const finalOutput = new AgentViewDataRowAdapter().toContracts(
        shows,
        expenses
      );
      setRowData(finalOutput);
    } catch (error) {
      setRowData([]);
      throw error;
    }
  };
  const cellSelection = useMemo(() => {
    return true;
  }, []);

  useEffect(() => {
    getData();
  }, []);
  return (
    <div style={containerStyle}>
      <div style={gridStyle} className={"ag-theme-quartz"}>
        <AgGridReact<AgentViewDataRow>
          rowData={rowData}
          columnDefs={columnDefs}
          sideBar={{ toolPanels: ["columns", "filters"] }}
          rowGroupPanelShow={"always"}
          enableCharts={true}
          cellSelection={cellSelection}
          defaultColDef={defaultColDef}
        />
      </div>
    </div>
  );
};

export default AgentViewAnalytics;
